import { z } from 'zod';

/**
 * API shapes of Phase 11 (06 §14): MCP servers and connections, skills and
 * their versions, teams, and the organization's web policy. Credentials go
 * in, never out: a server shows whether a key or a sign-in is present, not
 * its value (SA-040, QW-BUG-049).
 */

// MCP ---------------------------------------------------------------------------------------

const valueMap = z.record(z.string().min(1).max(64), z.string().max(8000));
export const mcpScope = z.enum(['tenant', 'workspace', 'user']);
export const mcpTransport = z.enum(['http', 'sse', 'stdio']);
export const mcpAuthKind = z.enum(['none', 'headers', 'oauth']);
export const mcpCatalogMode = z.enum(['available', 'default_on', 'required']);
export const mcpToolPolicy = z.enum(['allow', 'ask', 'deny']);

export const mcpConfigFields = z.object({
  transport: mcpTransport,
  url: z.string().trim().max(2000).nullable().optional(),
  command: z.string().trim().max(500).nullable().optional(),
  args: z.array(z.string().max(2000)).max(64).default([]),
  envNames: z.array(z.string().max(64)).max(32).default([]),
  headerNames: z.array(z.string().max(64)).max(16).default([]),
  authKind: mcpAuthKind.default('none'),
  oauthScope: z.string().trim().max(500).nullable().optional(),
  timeoutMs: z.number().int().min(1000).max(600_000).default(60_000),
});

/** Test a configuration before saving it (SA-043); values are used once and not kept. */
export const mcpTestBody = mcpConfigFields.extend({
  headers: valueMap.default({}),
  env: valueMap.default({}),
  /** local servers run in this code workspace's sandbox */
  workspaceId: z.uuid().optional(),
});

export const mcpServerBody = mcpConfigFields.extend({
  scope: mcpScope.default('user'),
  workspaceId: z.uuid().nullable().optional(),
  name: z.string().trim().regex(/^[a-z0-9][a-z0-9_-]{0,31}$/),
  displayName: z.string().trim().min(1).max(80),
  description: z.string().trim().max(1000).nullable().optional(),
  catalogMode: mcpCatalogMode.nullable().optional(),
  sharedCredentials: z.boolean().default(false),
  icon: z.string().trim().max(40).nullable().optional(),
  category: z.string().trim().max(40).nullable().optional(),
  toolPolicies: z.record(z.string().max(200), mcpToolPolicy).default({}),
  credentials: z.object({ headers: valueMap.optional(), env: valueMap.optional() }).optional(),
});

/** The transport stays what it was created with: another transport is another server (delete and add). */
export const mcpServerPatch = mcpServerBody
  .omit({ scope: true, workspaceId: true, name: true, transport: true })
  .partial()
  .refine((body) => Object.values(body).some((v) => v !== undefined), { message: 'nothing to update' });

export const mcpCredentialsBody = z.object({ headers: valueMap.optional(), env: valueMap.optional(), shared: z.boolean().default(false) });
export const mcpImportBody = z.object({ json: z.string().min(2).max(100_000) });
/** a path of the web app, or `app` (Phase 15c): the sign-in returns to the native app, which completes it with its token */
export const mcpOauthStartBody = z.object({ returnTo: z.union([z.literal('app'), z.string().max(500).regex(/^\/[^/\\]/)]).default('/connectors') });
export const mcpOauthCompleteBody = z.object({ state: z.string().min(16).max(128), code: z.string().min(1).max(2000) });
/** null clears the setting at that scope, so the wider one applies again */
export const enabledBody = z.object({
  enabled: z.boolean().nullable(),
  workspaceId: z.uuid().nullable().optional(),
  conversationId: z.uuid().nullable().optional(),
});
export const toolPolicyBody = z.object({ policy: mcpToolPolicy });
export const trustBody = z.object({ trusted: z.boolean() });
export const capabilityQuery = z.object({ workspaceId: z.uuid().optional(), conversationId: z.uuid().optional() });
/** the connections page "everywhere" (D-118): the user's own and the organization's, plus each workspace's own as that workspace sees them */
export const mcpListQuery = capabilityQuery.extend({ allWorkspaces: z.enum(['1', 'true']).optional() });

export type McpScope = z.infer<typeof mcpScope>;
export type McpTransport = z.infer<typeof mcpTransport>;
export type McpAuthKind = z.infer<typeof mcpAuthKind>;
export type McpCatalogMode = z.infer<typeof mcpCatalogMode>;
export type McpToolPolicy = z.infer<typeof mcpToolPolicy>;
export type McpServerBody = z.infer<typeof mcpServerBody>;
export type McpTestBody = z.infer<typeof mcpTestBody>;

export interface McpToolDto {
  name: string;
  title: string | null;
  description: string | null;
  readOnly: boolean;
  policy: McpToolPolicy;
}

export type McpBlockReason =
  | 'disabled'
  // Phase 16a (D-268): own connections are not on the person's plan
  | 'plan_required'
  | 'disabled_by_admin'
  | 'untrusted_workspace'
  | 'local_in_chat'
  | 'not_approved'
  | 'needs_auth'
  | 'needs_credentials'
  | 'not_discovered'
  | 'error';

export interface McpServerDto {
  id: string;
  scope: McpScope;
  workspaceId: string | null;
  name: string;
  displayName: string;
  description: string | null;
  transport: McpTransport;
  url: string | null;
  command: string | null;
  args: string[];
  envNames: string[];
  headerNames: string[];
  authKind: McpAuthKind;
  oauthScope: string | null;
  /** Phase 13: a sign-in client registered beforehand (the secret is never returned); its id for managers only */
  oauthClient: { clientId: string | null; hasSecret: boolean } | null;
  sharedCredentials: boolean;
  hasSharedCredentials: boolean;
  catalogMode: McpCatalogMode | null;
  trusted: boolean;
  timeoutMs: number;
  icon: string | null;
  category: string | null;
  tools: McpToolDto[];
  instructions: string | null;
  canManage: boolean;
  connection: {
    status: 'new' | 'ok' | 'needs_auth' | 'error';
    lastError: string | null;
    lastErrorAt: string | null;
    hasCredentials: boolean;
    signedIn: boolean;
    approved: boolean;
    connectedAt: string | null;
    lastUsedAt: string | null;
  } | null;
  enabled: boolean;
  enabledScope: 'conversation' | 'workspace' | 'user' | 'default' | 'required';
  usable: boolean;
  blockedReason: McpBlockReason | null;
  createdAt: string;
  updatedAt: string;
}

export type McpTestResultDto =
  | { ok: true; tools: McpToolDto[]; instructions: string | null; serverInfo: Record<string, unknown> | null }
  | { ok: false; code: string; message: string };

export interface McpImportDto {
  name: string | null;
  config: z.infer<typeof mcpConfigFields>;
  env: Record<string, string>;
  headers: Record<string, string>;
  warnings: string[];
}

// Skills ------------------------------------------------------------------------------------

export const skillLevel = z.enum(['organization', 'team', 'user', 'workspace']);
export const skillCatalogMode = z.enum(['available', 'default_on', 'required']);

export const skillForm = z.object({
  name: z.string().trim().min(1).max(64),
  description: z.string().trim().min(1).max(1024),
  whenToUse: z.string().trim().max(1000).optional(),
  argumentHint: z.string().trim().max(200).optional(),
  allowedTools: z.array(z.string().trim().min(1).max(300)).max(30).optional(),
  body: z.string().max(60_000),
});

/** Create or edit: the raw SKILL.md, or the form fields it is written from (SA-054). */
export const skillBody = z
  .object({
    level: skillLevel.default('user'),
    workspaceId: z.uuid().nullable().optional(),
    teamId: z.uuid().nullable().optional(),
    skillMd: z.string().max(70_000).optional(),
    form: skillForm.optional(),
    changeNote: z.string().trim().max(1000).optional(),
    /** organization library: an admin publishes at once, a member proposes for review */
    publish: z.boolean().optional(),
    source: z.enum(['created', 'conversation']).default('created'),
  })
  .refine((body) => Boolean(body.skillMd) !== Boolean(body.form), { message: 'give either skillMd or form' });

export const skillUpdateBody = z
  .object({
    skillMd: z.string().max(70_000).optional(),
    form: skillForm.optional(),
    changeNote: z.string().trim().max(1000).optional(),
    publish: z.boolean().optional(),
  })
  .refine((body) => Boolean(body.skillMd) !== Boolean(body.form), { message: 'give either skillMd or form' });

/** `/skill add <source>` and the add dialog: look first, install after (D-13). */
export const skillPreviewBody = z.object({
  source: z.string().trim().min(1).max(2000),
  /** a folder inside the source when it holds several skills */
  folder: z.string().trim().max(500).optional(),
});
export const skillInstallBody = z.object({
  previewId: z.string().min(16).max(80),
  level: skillLevel.default('user'),
  workspaceId: z.uuid().nullable().optional(),
  teamId: z.uuid().nullable().optional(),
  /** update this skill with the previewed content (a new version) */
  skillId: z.uuid().optional(),
  publish: z.boolean().optional(),
});
export const skillRollbackBody = z.object({ version: z.number().int().positive() });
export const skillReviewBody = z.object({ decision: z.enum(['approve', 'reject']), note: z.string().trim().max(1000).optional() });
export const skillPatchBody = z.object({ catalogMode: skillCatalogMode });
export const skillDraftBody = z.object({ hint: z.string().trim().max(1000).optional() });
export const skillUsageQuery = z.object({ days: z.coerce.number().int().min(1).max(365).default(30) });

export type SkillLevel = z.infer<typeof skillLevel>;
export type SkillForm = z.infer<typeof skillForm>;

export interface SkillPermissionsDto {
  commands: string[];
  ownScripts: boolean;
  reads: string[];
  ignored: { entry: string; reason: string }[];
}

export interface SkillFileDto {
  path: string;
  size: number;
  kind: 'skill' | 'script' | 'template' | 'reference' | 'other';
}

export interface SkillDto {
  /** the skill id, or builtin:<name> */
  key: string;
  id: string;
  name: string;
  description: string;
  whenToUse: string | null;
  argumentHint: string | null;
  level: 'builtin' | SkillLevel;
  teamId: string | null;
  teamName: string | null;
  workspaceId: string | null;
  version: number | null;
  latestVersion: number | null;
  latestStatus: 'draft' | 'in_review' | 'published' | 'rejected' | null;
  catalogMode: z.infer<typeof skillCatalogMode> | null;
  /** a team skill: is the user in the team (its mode applies to them)? null for the other levels (D-117) */
  teamMember: boolean | null;
  modelInvocable: boolean;
  userInvocable: boolean;
  enabled: boolean;
  enabledScope: 'conversation' | 'workspace' | 'user' | 'default' | 'required';
  shadowedBy: { key: string; level: string } | null;
  hides: { key: string; level: string }[];
  permissions: SkillPermissionsDto;
  hooks: string[];
  warnings: { code: string; message: string }[];
  /** what listing it costs per request, and what loading its instructions costs (MM-019) */
  listingTokens: number;
  bodyTokens: number;
  fileCount: number;
  canManage: boolean;
  source: { type: string; url?: string; ref?: string; commit?: string; path?: string; installedAt?: string; updatedAt?: string } | null;
  updatedAt: string | null;
}

export interface SkillVersionDto {
  id: string;
  version: number;
  status: 'draft' | 'in_review' | 'published' | 'rejected';
  changeNote: string | null;
  createdAt: string;
  createdByName: string | null;
  reviewNote: string | null;
  published: boolean;
  bodyChars: number;
  fileCount: number;
}

export interface SkillDetailDto extends SkillDto {
  skillMd: string;
  body: string;
  files: SkillFileDto[];
  versions: SkillVersionDto[];
}

export interface SkillPreviewDto {
  previewId: string;
  source: string;
  name: string;
  description: string;
  whenToUse: string | null;
  files: SkillFileDto[];
  permissions: SkillPermissionsDto;
  hooks: string[];
  warnings: { code: string; message: string }[];
  bodyChars: number;
  bodyTokens: number;
  commit: string | null;
  /** a source with several skills: choose one and preview again */
  candidates: string[];
  /** a skill of the same name the caller already has at that level */
  existing: { key: string; level: string; version: number | null } | null;
  /** from the frontmatter or a LICENSE file; open: null when nothing says */
  license: { name: string | null; open: boolean | null; from: 'frontmatter' | 'file' | null };
  skillMd: string;
}

export interface SkillUsageDto {
  key: string;
  name: string;
  level: string;
  runs: number;
  users: number;
  tokens: number;
  lastUsedAt: string | null;
}

export interface SkillDraftDto {
  skillMd: string;
  form: SkillForm;
  /** the numbered steps of the draft, shown before it opens in the editor */
  steps: string[];
  /** skills the conversation used */
  skills: string[];
}

/** "Kaydetmeden dene": a test turn with a draft skill (docs/design/mockups/SkillYaz.dc.html). */
export const skillTryBody = z.object({
  skillMd: z.string().min(1).max(60_000),
  prompt: z.string().trim().min(1).max(2000),
  workspaceId: z.uuid().optional(),
});

export interface SkillTryDto {
  /** the assistant loaded the draft by itself, from its description */
  chosen: boolean;
  /** another skill it reached for instead, if any */
  other: string | null;
  durationMs: number;
  text: string;
}

// Teams ------------------------------------------------------------------------------------

export const teamBody = z.object({ name: z.string().trim().min(1).max(80) });
export const teamMemberBody = z.object({ role: z.enum(['lead', 'member']) });

export interface TeamDto {
  id: string;
  name: string;
  memberCount: number;
  myRole: 'lead' | 'member' | null;
  createdAt: string;
  /** Phase 13d-2: the team of a directory domain (kept by the directory, not changed by hand) */
  domainId: string | null;
}

export interface TeamMemberDto {
  userId: string;
  displayName: string;
  email: string;
  role: 'lead' | 'member';
}

// Web policy --------------------------------------------------------------------------------

export const webPolicyBody = z
  .object({
    webSearchProvider: z.enum(['platform', 'searxng', 'brave', 'tavily', 'off']).optional(),
    webSearchUrl: z.string().trim().url().max(500).nullable().optional(),
    webFetchEnabled: z.boolean().optional(),
    allowDomains: z.array(z.string().trim().min(1).max(253)).max(200).optional(),
    denyDomains: z.array(z.string().trim().min(1).max(253)).max(200).optional(),
    skillExternalSources: z.boolean().optional(),
    mcpUserServers: z.boolean().optional(),
  })
  .refine((body) => Object.values(body).some((v) => v !== undefined), { message: 'nothing to update' });
export const searchKeyBody = z.object({ apiKey: z.string().trim().min(8).max(500).nullable() });

export interface WebPolicyDto {
  webSearchProvider: 'platform' | 'searxng' | 'brave' | 'tavily' | 'off';
  webSearchUrl: string | null;
  hasSearchKey: boolean;
  webFetchEnabled: boolean;
  allowDomains: string[];
  denyDomains: string[];
  skillExternalSources: boolean;
  mcpUserServers: boolean;
  platformSearch: boolean;
}
